define(
    [
        'underscore',
        'assets/js/common/router/index',
        'assets/js/modules/design/designModel',
        'assets/js/modules/option/optionModel',
        'assets/js/modules/option/optionValueModel',
        'assets/js/modules/designFacePreview/model/designFacePreviewModel',
        'assets/js/modules/design/designFaceModel',
        'assets/js/modules/design/designFaceProcessModel',
        'assets/js/modules/product/model/productGroupModel'
    ],
    function(
        _,
        router,
        DesignModel,
        OptionModel,
        OptionValueModel,
        DesignFacePreviewModel,
        DesignFaceModel,
        DesignFaceProcessModel,
        ProductGroupModel
    ) {
        var INSERT_COLORS_PATH = '/assets/images/insertColors/<%= type %><%= val %>.png';
        var MAGNETS_BACKSIDE_PATH = '/assets/images/insertColors/magnetsSideColor.png';
        var BLANK_BACKSIDE_PATH = '/assets/images/insertColors/blank.png';
        var GLOSSED_SUBSTRATES = ['80lbs_gloss', '100lbs_gloss', '100_aqueous_finish'];
        var DVV_FACE_TYPE_FRONT = 'front';
        var DVV_FACE_TYPE_BACK = 'back';
        var DVV_PROCESS_REGULAR = 'regular';
        var DVV_PROCESS_SPOT_UV = 'spotUV';
        var DVV_PROCESS_TEXTURE = 'KRE8-3D';
        var DVV_PROCESS_FOIL_SILVER = 'foil_silver';
        var DVV_PROCESS_FOIL_GOLD = 'foil_gold';
        var DVV_SIDE_ONE = 'i0';
        var DVV_SIDE_TWO = 'i1';
        var DVV_SIDE_ONE_DISTANCE = 'w0';
        var DVV_SIDE_TWO_DISTANCE = 'w1';
        var DVV_FOLDING_DIRECTION = 'd';
        var DVV_FOLDING_ORDER = 'o';
        var DVV_WRAP_SIZE = 'ws';
        var DVV_SECOND_FOLD = 'f1';

        var ASSETS_VERSION = ONP.config.assetsVersion;
        var FULL_UV_PROCESS = {
            type: DVV_PROCESS_SPOT_UV,
            gloss: 0.3
        };

        return {
            /**
             * @param {DesignModel} design
             * @param {Object} params
             * @returns {Object}
             */
            parse: function(design, params) {
                let foldingMap = {};
                if (params.designerConfig !== undefined && params.designerConfig.foldingMap !== undefined) {
                    foldingMap = params.designerConfig.foldingMap;
                }

                var opts = design.get('optionsContext');
                var faces = this.extractFaces(design, params.designerConfig);
                var thickness = this.extractThickness(design);
                var diecut = this.extractDiecut(design, params.circleCanvas);
                var folding = this.extractFolding(design, params.inverseFolding, foldingMap);
                var foldOrigin = this.extractFoldOrigin(opts);

                var dvvObj = {
                    width: design.get('width'),
                    height: design.get('height'),
                    thickness: thickness.value,
                    diecut: diecut,
                    fold: folding.foldCode,
                    foldConfig: folding.config,
                    foldRotation: folding.rotation,
                    faces: faces
                };

                if (params.designerConfig && params.designerConfig['dvv-z-zoom']) {
                    dvvObj.camera = {
                        position: { x: 0, y: 0, z: params.designerConfig['dvv-z-zoom'] },
                        animation: [{ m: 0 }],
                        path: [{ z: params.designerConfig['dvv-z-zoom'] }, { rx: 0 }]
                    };
                }

                if (folding.folds) {
                    dvvObj.folds = folding.folds;
                }

                if (thickness.src) {
                    dvvObj.thicknessSrc = thickness.src;
                }

                if (foldOrigin) {
                    dvvObj.foldOrigin = foldOrigin;
                }

                return dvvObj;
            },

            /**
             * @param {DesignModel} design
             * @param {object} designerConfig
             * @returns {*[]}
             */
            extractFaces: function(design, designerConfig) {
                var dvvFrontFace = {
                    faceType: DVV_FACE_TYPE_FRONT,
                    processes: this.getDvvFrontProcesses(design),
                    rotation: this.getFrontFaceRotation(design.get('orientation'), designerConfig)
                };
                var dvvBackFace = {
                    faceType: DVV_FACE_TYPE_BACK,
                    processes: this.getDvvBackProcesses(design),
                    rotation: this.getBackFaceRotation(design.get('orientation'), designerConfig)
                };

                return [dvvFrontFace, dvvBackFace];
            },

            /**
             * @param {DesignModel} design
             * @param {boolean} isInverse
             * @param {object} foldingMap
             * @returns {{foldCode: *, config: string, rotation: string}}
             */
            extractFolding: function(design, isInverse, foldingMap) {
                let opts = design.get('optionsContext');
                let folding = opts.get('folding');
                const scoring = opts.get('scoring');
                const productSlug = design.get('productContext').get('slug');
                if (
                    folding == OptionValueModel.NAME_NONE_FOLD &&
                    productSlug.indexOf('gc') === 0 &&
                    scoring == OptionValueModel.NAME_SCORING_BI_FOLD
                ) {
                    folding = OptionValueModel.NAME_BI_FOLD;
                }
                let rotation = {
                    horizontal: 0,
                    vertical: 90
                };

                let extractedFolding = {
                    foldCode: this.getFoldCode(folding, isInverse, foldingMap),
                    config: '',
                    rotation: rotation[design.get('orientation') || DesignModel.ORIENTATION_HORIZONTAL]
                };

                var folds = this.getFoldSizes(design.get('productContext').get('folds'), folding, isInverse);
                if (folds) {
                    extractedFolding.folds = folds;
                }

                if (opts.get('folds') !== undefined) {
                    extractedFolding.folds = opts.get('folds');
                }

                return extractedFolding;
            },

            /**
             *
             * @param {OptionsContext} opts
             * @returns {Object|undefined}
             */
            extractFoldOrigin: function(opts) {
                if (opts.get('foldOrigin')) {
                    return opts.get('foldOrigin');
                }
            },

            /**
             *
             * @param {DesignModel} design
             * @param {boolean} isEllipse
             * @returns {Object}
             */
            extractDiecut: function(design, isEllipse) {
                var opts = design.get('optionsContext');
                var defaultOpts = design.get('productContext').get('defaultOptions');

                var hasRoundedCorners =
                    opts.get(OptionModel.NAME_CORNERS) === OptionValueModel.NAME_ROUNDED_CORNERS ||
                    defaultOpts.get(OptionModel.NAME_CORNERS) === OptionValueModel.NAME_ROUNDED_CORNERS;

                if (opts.get('diecut') !== undefined) {
                    return opts.get('diecut');
                }

                return {
                    type: isEllipse ? 'ellipse' : 'rect',
                    radius: hasRoundedCorners ? 40 : 0
                };
            },

            /**
             * @param {DesignModel} design
             * @returns {{src: string, value: number}}
             */
            extractThickness: function(design) {
                var getFillingPath = _.template(INSERT_COLORS_PATH);
                var opts = design.get('optionsContext');
                var substrate = opts.get('substrate');
                var filling = opts.get('filling');
                var thickness = opts.get('thickness');
                var productSlug = design.get('productContext').get('slug');
                var src = '';
                var value = 2;

                if (substrate == '100lbs_accent_opaque') {
                    value = this.getThicknessValue(thickness);
                } else if (substrate == '120_opaque') {
                    value = 8;
                    src = getFillingPath({ type: 'white_filling', val: 8 });
                }

                if (filling) {
                    if (substrate == '100lbs_accent_opaque') {
                        src = getFillingPath({ type: filling, val: value });
                    }
                }

                if (!design.get('productContext').get('isCrossSells')) {
                    if (productSlug.indexOf('sandwich') !== -1) {
                        value = this.getThicknessValue(thickness);
                        src = getFillingPath({ type: filling, val: value });
                    }
                    if (productSlug.indexOf('fat') !== -1) {
                        src = getFillingPath({ type: 'white_filling', val: 8 });
                    }
                }

                return {
                    src: src,
                    value: value
                };
            },

            // Helpers
            getFoldCode: function(foldingTypeId, isInverse, foldingMap) {
                /** See @link(http://cip4.org/documents/jdf_specifications/html2/JDF1/Resources/Resources.htm#XREF_84554_Fold_Catalog_part_1) */
                let jdfCodes = {};
                // designerConfig.dvvConfig

                if (isInverse) {
                    jdfCodes[OptionValueModel.NAME_NONE_FOLD] = '';
                    jdfCodes[OptionValueModel.NAME_TRI_FOLD] = 'F6-8';
                    jdfCodes[OptionValueModel.NAME_Z_FOLD] = 'F6-2';
                    jdfCodes[OptionValueModel.NAME_BI_FOLD] = 'F4-1';
                } else {
                    jdfCodes[OptionValueModel.NAME_NONE_FOLD] = '';
                    jdfCodes[OptionValueModel.NAME_TRI_FOLD] = 'F6-4';
                    jdfCodes[OptionValueModel.NAME_Z_FOLD] = 'F6-1';
                    jdfCodes[OptionValueModel.NAME_BI_FOLD] = 'F4-1';
                }
                jdfCodes = {
                    ...jdfCodes,
                    ...foldingMap
                }

                return jdfCodes[foldingTypeId]; // flat folding default == F2-1
            },

            /**
             * @param {string} foldCode
             * @param {boolean} isInverse
             * @param {object} foldingMap
             * @returns {string|null}
             */
            getFoldIdByFoldCode: function(foldCode, isInverse, foldingMap) {
                if (!foldCode) {
                    return OptionValueModel.NAME_NONE_FOLD;
                }

                /** See @link(http://cip4.org/documents/jdf_specifications/html2/JDF1/Resources/Resources.htm#XREF_84554_Fold_Catalog_part_1) */
                let foldIds = {
                    'No-Fold': OptionValueModel.NAME_NONE_FOLD
                };

                if (isInverse) {
                    foldIds['F6-8'] = OptionValueModel.NAME_TRI_FOLD;
                    foldIds['F6-2'] = OptionValueModel.NAME_Z_FOLD;
                    foldIds['F4-1'] = OptionValueModel.NAME_BI_FOLD;
                } else {
                    foldIds['F6-4'] = OptionValueModel.NAME_TRI_FOLD;
                    foldIds['F6-1'] = OptionValueModel.NAME_Z_FOLD;
                    foldIds['F4-1'] = OptionValueModel.NAME_BI_FOLD;
                }
                for(let prop in foldingMap) {
                    foldIds[foldingMap[prop]] = prop;
                }

                return foldIds.hasOwnProperty(foldCode) ? foldIds[foldCode] : null;
            },

            getFoldSizes: function(foldsDimensions, foldingTypeId, isInverse) {
                var folds = null;
                if (!foldsDimensions || [OptionValueModel.NAME_Z_FOLD, OptionValueModel.NAME_TRI_FOLD].indexOf(foldingTypeId) == -1) {
                    return folds;
                }

                var triFoldsDimensions = foldsDimensions[OptionValueModel.NAME_TRI_FOLD][DesignFacePreviewModel.FACE_NAMES.BACK];

                var faceZeroOrder = 0;
                var faceOneOrder = 1;
                var faceZeroDirection = 0;
                var faceOneDirection = 0;
                var faceZeroWrapSize = 1;
                var faceOneWrapSize = 2;

                if (isInverse) {
                    faceZeroOrder = 1;
                    faceOneOrder = 0;
                    faceZeroWrapSize = 2;
                    faceOneWrapSize = 1;
                }

                if (foldingTypeId === OptionValueModel.NAME_Z_FOLD) {
                    faceZeroOrder = 0;
                    faceOneOrder = 0;
                    faceOneDirection = 1;
                    if (isInverse) {
                        faceZeroDirection = 1;
                        faceOneDirection = 0;
                    }
                }

                var faceZeroFoldingLine =
                    triFoldsDimensions.right / (triFoldsDimensions.left + triFoldsDimensions.center + triFoldsDimensions.right);
                var faceOneFoldingLine = triFoldsDimensions.center / (triFoldsDimensions.center + triFoldsDimensions.left);

                var secondFold = [];
                secondFold[DVV_SIDE_ONE] = 1;
                secondFold[DVV_SIDE_TWO] = 3;
                secondFold[DVV_SIDE_ONE_DISTANCE] = faceOneFoldingLine;
                secondFold[DVV_SIDE_TWO_DISTANCE] = 1 - faceOneFoldingLine;
                secondFold[DVV_FOLDING_DIRECTION] = faceOneDirection;
                secondFold[DVV_FOLDING_ORDER] = faceOneOrder;
                secondFold[DVV_WRAP_SIZE] = faceOneWrapSize;

                folds = [];
                folds[DVV_SIDE_ONE] = 0;
                folds[DVV_SIDE_TWO] = 2;
                folds[DVV_SIDE_ONE_DISTANCE] = faceZeroFoldingLine;
                folds[DVV_SIDE_TWO_DISTANCE] = 1 - faceZeroFoldingLine;
                folds[DVV_FOLDING_DIRECTION] = faceZeroDirection;
                folds[DVV_FOLDING_ORDER] = faceZeroOrder;
                folds[DVV_WRAP_SIZE] = faceZeroWrapSize;
                folds[DVV_SECOND_FOLD] = secondFold;

                return folds;
            },

            /**
             * @param {DesignModel} design
             * @return {Object[]}
             */
            getDvvFrontProcesses: function(design) {
                var dvvProcesses = [];
                var opts = design.get('optionsContext');
                var frontCoating = opts.get('frontCoating');
                var frontFoilColor = opts.get('frontFoilColor');
                var face = design.get('faces').get(DesignFaceModel.TYPE_FRONT);

                face.get('processes').forEach(process => {
                    switch (process.get('type')) {
                        case DesignFaceProcessModel.TYPE_REGULAR:
                            dvvProcesses.push({
                                type: DVV_PROCESS_REGULAR,
                                src: this.getFrontRegularPath(process, design)
                            });

                            if (
                                frontCoating === OptionValueModel.NAME_FRONT_COATING_FULL_UV ||
                                this.isGlossedSubstrate(opts.get('substrate'))
                            ) {
                                dvvProcesses.push({
                                    ...FULL_UV_PROCESS,
                                    src: this.getFrontRegularPath(process, design)
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_SPOT_UV:
                            if (frontCoating === OptionValueModel.NAME_FRONT_COATING_SPOT_UV) {
                                dvvProcesses.push({
                                    type: DVV_PROCESS_SPOT_UV,
                                    src: process.get('src')
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_TEXTURE:
                            if (frontCoating === OptionValueModel.NAME_FRONT_COATING_TEXTURE) {
                                dvvProcesses.push({
                                    type: DVV_PROCESS_TEXTURE,
                                    src: process.get('src')
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_FOIL:
                            if (frontCoating === OptionValueModel.NAME_FRONT_COATING_FOIL) {
                                var processType;

                                switch (frontFoilColor) {
                                    case OptionValueModel.NAME_FRONT_FOIL_GOLD:
                                        processType = DVV_PROCESS_FOIL_GOLD;
                                        break;
                                    case OptionValueModel.NAME_FRONT_FOIL_SILVER:
                                        processType = DVV_PROCESS_FOIL_SILVER;
                                        break;
                                }

                                if (processType) {
                                    dvvProcesses.push({
                                        type: processType,
                                        src: process.get('src')
                                    });
                                }
                            }
                            break;
                    }
                });

                return dvvProcesses;
            },

            /**
             * @param {DesignModel} design
             * @return {Object[]}
             */
            getDvvBackProcesses: function(design) {
                var dvvProcesses = [];
                var opts = design.get('optionsContext');
                var backCoating = opts.get('backCoating');
                var backFoilColor = opts.get('backFoilColor');
                var productGroupSlug = design.get('productContext').get('group');
                var face = design.get('faces').get(DesignFaceModel.TYPE_BACK);

                face.get('processes').forEach(process => {
                    switch (process.get('type')) {
                        case DesignFaceProcessModel.TYPE_REGULAR:
                            dvvProcesses.push({
                                type: DVV_PROCESS_REGULAR,
                                src: this.getBackRegularPath(process, productGroupSlug)
                            });

                            if (
                                backCoating === OptionValueModel.NAME_BACK_COATING_FULL_UV ||
                                this.isGlossedSubstrate(opts.get('substrate'))
                            ) {
                                dvvProcesses.push({
                                    ...FULL_UV_PROCESS,
                                    src: this.getBackRegularPath(process, productGroupSlug)
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_SPOT_UV:
                            if (backCoating === OptionValueModel.NAME_BACK_COATING_SPOT_UV) {
                                dvvProcesses.push({
                                    type: DVV_PROCESS_SPOT_UV,
                                    src: process.get('src')
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_TEXTURE:
                            if (backCoating === OptionValueModel.NAME_BACK_COATING_TEXTURE) {
                                dvvProcesses.push({
                                    type: DVV_PROCESS_TEXTURE,
                                    src: process.get('src')
                                });
                            }
                            break;
                        case DesignFaceProcessModel.TYPE_FOIL:
                            if (backCoating === OptionValueModel.NAME_BACK_COATING_FOIL) {
                                var processType;

                                switch (backFoilColor) {
                                    case OptionValueModel.NAME_BACK_FOIL_GOLD:
                                        processType = DVV_PROCESS_FOIL_GOLD;
                                        break;
                                    case OptionValueModel.NAME_BACK_FOIL_SILVER:
                                        processType = DVV_PROCESS_FOIL_SILVER;
                                        break;
                                }

                                if (processType) {
                                    dvvProcesses.push({
                                        type: processType,
                                        src: process.get('src')
                                    });
                                }
                            }
                            break;
                    }
                });

                return dvvProcesses;
            },

            /**
             * @param {DesignFaceProcessModel} process
             * @param {DesignModel} design
             * @returns {string}
             */
            getFrontRegularPath: function(process, design) {
                var path = process.get('src');
                var fileId = process.get('fileId');
                var opts = design.get('optionsContext');

                if (opts.get('trimming') === OptionValueModel.NAME_NONE_TRIMMING) {
                    if (design.get('designerType') == DesignModel.DESIGNER_TYPE_DVS && fileId) {
                        path = router.generate('/dynamic-renderer/non-trimmed-dvs/{id}', {
                            id: fileId
                        });
                    } else if (fileId && process.get('sessionId')) {
                        // we have to pass session id manually, because dvv doesn't send cookies when requesting the image in CHROME
                        path = router.generate('/dynamic-renderer/non-trimmed-upload/{fileUploadedId}/{sessionId}', {
                            fileUploadedId: fileId,
                            sessionId: process.get('sessionId')
                        });
                    } else if (design.get('pgId')) {
                        path = router.generate('/dynamic-renderer/non-trimmed/{pgId}', {
                            pgId: design.get('pgId')
                        });
                    }
                }

                return path ? path + '?' + ASSETS_VERSION : '';
            },

            /**
             * @param {DesignFaceProcess} process
             * @param {string} productGroupSlug
             * @returns {string}
             */
            getBackRegularPath: function(process, productGroupSlug) {
                var path = process.get('src');
                if (productGroupSlug === ProductGroupModel.SLUG_MAGNETS) {
                    path = MAGNETS_BACKSIDE_PATH;
                } else if (path && path.length) {
                    path = process.get('src');
                } else {
                    path = BLANK_BACKSIDE_PATH;
                }

                return path ? path + '?' + ASSETS_VERSION : '';
            },

            /**
             *
             * @param {string} thickness
             * @returns {number}
             */
            getThicknessValue: function(thickness) {
                return thickness === OptionValueModel.NAME_SINGLE_THICKNESS ? 8 : 12;
            },

            /**
             * @param {string} substrateName
             * @returns {boolean}
             */
            isGlossedSubstrate: function(substrateName) {
                return _.contains(GLOSSED_SUBSTRATES, substrateName);
            },

            /**
             * @param {string} orientation
             * @param {object} designerConfig
             * @returns {number}
             */
            getFrontFaceRotation: function(orientation, designerConfig) {
                return this._getRotation(orientation, designerConfig, 0);
            },

            /**
             * @param {string} orientation
             * @param {object} designerConfig
             * @returns {number}
             */
            getBackFaceRotation: function(orientation, designerConfig) {
                return this._getRotation(orientation, designerConfig, 1);
            },

            /**
             * @param {string} orientation
             * @param {object} designerConfig
             * @param {number} index
             * @returns {number}
             */
            _getRotation: function(orientation, designerConfig, index) {
                try {
                    if (designerConfig !== undefined && orientation === designerConfig.dvvConfig.rotationAngle.orientation) {
                        return designerConfig.dvvConfig.rotationAngle.rotation[index];
                    }

                    return 0;
                } catch (e) {
                    return 0;
                }
            }
        };
    }
);
